import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { usePropertyTaxUploadStore } from '@/stores/propertyTaxUploadStore'
import { UploadStatus } from '@/types/aeroUpload/aeroUpload'
import { useCommonModalStore } from '@/stores/commonModalStore'
import {
  HomeElevationType,
  type FlightProjectData,
  type UploadFileData
} from '@/types/propertyTaxUpload/propertyTaxUpload'
import {
  ACCEPTED_FILE_NAME_WITH_EXTENSION_LENGTH,
  isAcceptedExtension
} from '@/constants/ui/propertyTaxUpload/uploadAccepts'

export const usePartsCombination = () => {
  const isDragOver = ref(false)
  const fileInput = ref<HTMLInputElement | null>(null)
  const uploadStore = usePropertyTaxUploadStore()
  const { isUploading, uploadFileList, flightProjectData, validateErrors } =
    storeToRefs(uploadStore)
  const commonModalStore = useCommonModalStore()

  const uploadButtonDisabled = computed(
    () =>
      isUploading.value ||
      uploadFileList.value.filter(d => d.status !== UploadStatus.COMPLETE).length === 0
  )

  const cancelButtonDisabled = computed(() => uploadFileList.value.length === 0)

  const dragOver = () => (isDragOver.value = true)

  const dragLeave = () => (isDragOver.value = false)

  const dropFile = (event: DragEvent) => {
    event.preventDefault()
    isDragOver.value = false
    if (event.dataTransfer) {
      const droppedFiles = Array.from(event.dataTransfer.files)
      const zipFiles = droppedFiles.filter(file => isAcceptedExtension(file.name))
      if (zipFiles.length === 0) {
        // zipfile以外はreturn
        return
      }
      if (checkFileNameLength(zipFiles)) {
        return
      }
      // ファイルリストに追加
      uploadStore.addFiles(...zipFiles)
    }
  }

  /**
   * ドロップエリアクリック時にファイルインプットイベントを起こす
   */
  const onClickArea = () => {
    if (fileInput.value) {
      fileInput.value.click()
    }
  }

  const onFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    if (!input.files) return
    const droppedFiles = Array.from(input.files)
    const zipFiles = droppedFiles.filter(file => file.name.toLowerCase().endsWith('.zip'))
    if (zipFiles.length === 0) {
      // zipfile以外はreturn
      return
    }
    if (checkFileNameLength(zipFiles)) {
      return
    }
    // ファイルリストに追加
    uploadStore.addFiles(...zipFiles)
    input.value = ''
  }

  /**
   * ファイル名の長さをチェック
   * @param files
   * @returns
   */
  const checkFileNameLength = (files: File[]): boolean => {
    for (const file of files) {
      if (file.name.length > ACCEPTED_FILE_NAME_WITH_EXTENSION_LENGTH) {
        // 拡張子を除いたファイル名の長さ
        const file_name_length = ACCEPTED_FILE_NAME_WITH_EXTENSION_LENGTH - 4
        // アップロード確認ダイアログ表示
        commonModalStore.openAlertModal({
          body:
            'アップロードを行う空撮画像のファイル名は拡張子を除いて' +
            file_name_length +
            '字以内にしてください。'
        })
        return true
      }
    }
    return false
  }

  /**
   * フライト開始地点情報を表示用にフォーマットする
   * @param flightProjectData
   * @returns
   */
  const formatHomeElevation = (flightProjectData: FlightProjectData): string => {
    switch (flightProjectData.homeElevationType) {
      case HomeElevationType.LATLNG:
        return `経度 ${flightProjectData.homeElevationLatlng.latitude} / 経度 ${flightProjectData.homeElevationLatlng.longitude}`
      case HomeElevationType.ALTITUDE:
        return `${flightProjectData.homeElevationValue}m`
      case HomeElevationType.FILE_NAME:
        return `${flightProjectData.homeElevationImgFileName}`
      default:
        return '指定なし'
    }
  }

  /**
   * アップロード確認ダイアログメッセージの生成
   * @param flightProjectData フライトプロジェクトデータ
   * @param files アップロードファイルデータ
   * @returns
   */
  const createUploadConfirmMessage = (
    flightProjectData: FlightProjectData,
    files: UploadFileData[]
  ): string => {
    const projectName = uploadStore.getProjectName(flightProjectData.projectId)
    const flightAltitudeStr = flightProjectData.flightAltitude
      ? `${flightProjectData.flightAltitude}m`
      : '指定なし'
    const homeElevationStr = formatHomeElevation(flightProjectData)
    const fileListStr = files
      .filter(d => d.status !== UploadStatus.COMPLETE)
      .map(f => `　${f.fileName}`)
      .join('<br>')
    return `
      以下の情報でファイルをアップロードします。<br>よろしいですか？<br><br>
      プロジェクト：${projectName}<br>
      フライト高度：${flightAltitudeStr}<br>
      フライト開始地点：${homeElevationStr}<br>
      アップロードファイル：<br>
      ${fileListStr}
        `
  }

  /**
   * 入力値バリデーション
   * @returns 検証結果
   */
  const isValidInput = (): boolean => {
    uploadStore.resetValidateErrors()

    if (flightProjectData.value.homeElevationType !== HomeElevationType.NOT_APPLICABLE) {
      switch (flightProjectData.value.homeElevationType) {
        case HomeElevationType.LATLNG:
          if (
            !flightProjectData.value.homeElevationLatlng.latitude ||
            !flightProjectData.value.homeElevationLatlng.longitude
          ) {
            validateErrors.value.homeElevationLatlng = 'フライト開始地点の緯度経度は必須です。'
            return false
          }
          break
        case HomeElevationType.ALTITUDE:
          if (!flightProjectData.value.homeElevationValue) {
            validateErrors.value.homeElevationValue = 'フライト開始地点の高度は必須です。'
            return false
          }
          break
        case HomeElevationType.FILE_NAME:
          if (!flightProjectData.value.homeElevationImgFileName) {
            validateErrors.value.homeElevationImgFileName =
              'フライト開始地点の撮影ファイル名は必須です。'
            return false
          }
          break
      }
    }

    return true
  }

  /** アップロードボタン押下時処理 */
  const upload = async () => {
    if (!isValidInput()) {
      return
    }

    // アップロード確認ダイアログ表示
    const res = await commonModalStore.openConfirmModalAsync({
      body: createUploadConfirmMessage(flightProjectData.value, uploadFileList.value)
    })
    if (!res) {
      return
    }

    await uploadStore.executeUpload()
  }

  /** キャンセルボタン押下時処理 */
  const cancel = async () => {
    if (isUploading.value) {
      // アップロード中の場合はアップロードを中断
      const res = await commonModalStore.openConfirmModalAsync({
        body: 'アップロードをキャンセルします。よろしいですか？'
      })
      if (res) {
        uploadStore.abortUpload()
      }
    } else {
      // アップロード中でなければファイルリストをクリア
      const res = await commonModalStore.openConfirmModalAsync({
        body: '一覧表をクリアします。よろしいですか？'
      })
      if (res) {
        uploadStore.clearFiles()
      }
    }
  }

  return {
    isDragOver,
    fileInput,
    isUploading,
    uploadButtonDisabled,
    cancelButtonDisabled,
    dragOver,
    dragLeave,
    dropFile,
    onClickArea,
    onFileChange,
    upload,
    cancel
  }
}
